import { useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { usePortal } from "./PortalContext";

const TYPES = [["Speech-Language", "Speech-language"], ["OT", "Occupational therapy"], ["PT", "Physical therapy"], ["Psychological", "Psychological"], ["Educational", "Educational"], ["FBA", "FBA"], ["Screening", "Screening"], ["Other", "Other"]];
const GRADES = ["Pre-K", "K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "Ungraded"];
const CONSENT = [["signed", "Signed consent on file (upload below)"], ["verbal", "Verbal consent from the family"], ["not_yet", "Not yet — TAG should wait for consent"]];

export default function PortalNewRequest({ base }) {
  const portal = usePortal();
  const navigate = useNavigate();
  const file = useRef(null);
  const [form, setForm] = useState({ student_initials: "", grade: "", evaluation_types: [], reason: "", consent_status: "not_yet", requested_due_date: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));
  const toggleType = (key) => setForm((f) => ({ ...f, evaluation_types: f.evaluation_types.includes(key) ? f.evaluation_types.filter((t) => t !== key) : [...f.evaluation_types, key] }));

  async function submit(event) {
    event.preventDefault();
    if (!form.student_initials.trim()) {
      setError("Enter the student's initials (for example D.B.).");
      return;
    }
    if (form.evaluation_types.length === 0) {
      setError("Choose at least one evaluation type.");
      return;
    }
    if (form.consent_status === "signed" && !file.current?.files?.[0]) {
      setError("Attach the signed consent form, or choose a different consent status.");
      return;
    }
    setBusy(true);
    setError("");
    const body = new FormData();
    Object.entries({ ...form, evaluation_types: form.evaluation_types.join(",") }).forEach(([k, v]) => body.append(k, v));
    if (file.current?.files?.[0]) body.append("consent_file", file.current.files[0]);
    try {
      const response = await api.post("/client-portal/requests", body, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="cp-page cp-narrow" data-testid="portal-request-confirmation">
        <div className="cp-card cp-confirm">
          <CheckCircle2 size={36} />
          <h1 className="cp-h1">Request received</h1>
          <p>{done.message}</p>
          <p className="cp-muted">{done.student} · {done.evaluation_types.join(", ")}{done.consent_attached ? " · consent form attached" : ""}</p>
          <div className="cp-actions">
            <button type="button" className="cp-btn cp-btn-primary" data-testid="portal-request-view-tracker" onClick={() => navigate(`${base}/evaluations?open=${done.ids[0]}`)}>Open the tracker</button>
            <button type="button" className="cp-btn cp-btn-outline" data-testid="portal-request-another" onClick={() => { setDone(null); setForm({ student_initials: "", grade: "", evaluation_types: [], reason: "", consent_status: "not_yet", requested_due_date: "" }); }}>Submit another request</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-page cp-narrow" data-testid="portal-new-request">
      <h1 className="cp-h1">New request</h1>
      <p className="cp-lede">Tell TAG what you need. Use the student's initials only; TAG will collect the rest with the consent form.</p>
      {portal.preview && <p className="cp-inline-note" data-testid="portal-request-preview-note">Requests are disabled in client view preview.</p>}
      <form className="cp-card cp-form" onSubmit={submit} data-testid="portal-request-form">
        <div className="cp-form-row">
          <label>Student initials<input value={form.student_initials} maxLength={12} placeholder="D.B." data-testid="portal-request-initials" onChange={set("student_initials")} /></label>
          <label>Grade
            <select value={form.grade} data-testid="portal-request-grade" onChange={set("grade")}><option value="">Choose…</option>{GRADES.map((g) => <option key={g} value={g}>{g}</option>)}</select>
          </label>
        </div>
        <fieldset className="cp-fieldset" data-testid="portal-request-types">
          <legend>Evaluation type(s)</legend>
          <div className="cp-type-grid">
            {TYPES.map(([key, label]) => (
              <label key={key} className={`cp-check ${form.evaluation_types.includes(key) ? "is-on" : ""}`}>
                <input type="checkbox" checked={form.evaluation_types.includes(key)} data-testid={`portal-request-type-${key.toLowerCase()}`} onChange={() => toggleType(key)} />{label}
              </label>
            ))}
          </div>
        </fieldset>
        <label>Reason for referral<textarea value={form.reason} rows={4} maxLength={4000} placeholder="What prompted this request and what you hope to learn." data-testid="portal-request-reason" onChange={set("reason")} required /></label>
        <fieldset className="cp-fieldset" data-testid="portal-request-consent">
          <legend>Consent status</legend>
          {CONSENT.map(([key, label]) => (
            <label key={key} className="cp-radio"><input type="radio" name="consent" value={key} checked={form.consent_status === key} data-testid={`portal-request-consent-${key}`} onChange={set("consent_status")} />{label}</label>
          ))}
          <label className="cp-file">Consent form (PDF or image){form.consent_status === "signed" ? " — required" : " — optional"}<input ref={file} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="portal-request-consent-file" /></label>
        </fieldset>
        <label>Requested due date<input type="date" value={form.requested_due_date} data-testid="portal-request-due" onChange={set("requested_due_date")} /></label>
        {error && <p className="cp-error" data-testid="portal-request-error">{error}</p>}
        <div className="cp-actions">
          <button type="submit" className="cp-btn cp-btn-primary" data-testid="portal-request-submit" disabled={busy || portal.preview}>{busy ? "Sending…" : "Submit request"}</button>
          <Link to={base} className="cp-btn cp-btn-ghost">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
